/**
 * The offline control on a Maps or Sets index row: one pill, one glyph, two states.
 *
 * Not on the phone yet: an outlined pill you tap to download. On the phone: the same pill,
 * filled - the action completed (operator, 2026-09-25: "the download button should be the
 * same format as the downloaded icon", and it lives INSIDE the row, not under it).
 *
 * It sits over the row's own button (a button cannot hold a button), in the space the row
 * reserves with list-index__pill-slot. Maps and Sets download the same set - map, lineup,
 * your picks (downloadFestival) - so a festival reads as downloaded on both pages or neither.
 */

import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { onlineManager, useQueryClient } from '@tanstack/react-query';
import { ArrowDownToLine, Check, RotateCw } from 'lucide-react';
import { downloadFestival, isFestivalOffline } from '../lib/festivalOffline';
import { mapStore } from '../lib/map/mapStore';

/**
 * Give up on a download after this. With no service React Query pauses fetchQuery until it is back online, which left
 * the pill pulsing in "saving" forever; the jobs keep running underneath, so if they do land the pill still flips to
 * downloaded on its own.
 */
const DOWNLOAD_GIVE_UP_MS = 20_000;

function hasService() {
  if (!onlineManager.isOnline()) return false;
  return typeof navigator === 'undefined' || navigator.onLine !== false;
}

/** `tile`: on a Sets/Maps photo tile - the Events tile's Join button, same glass circle and corner (Sam, 2026-10-28:
 * the small pill was hard to see over a photo). Downloaded is a ✓, like Join's going state - the glyph says it. */
export default function FestivalDownloadPill({ eventId, eventName, userId, tile = false }) {
  const queryClient = useQueryClient();
  const [state, setState] = useState('idle'); // idle | saving | failed
  // Subscribed, not read once: the persisted cache restores after first paint, and the 36 h
  // prefetch can land while the list is open.
  const subscribe = useCallback(
    (cb) => {
      const stopQueries = queryClient.getQueryCache().subscribe(cb);
      const stopMaps = mapStore.subscribe(cb);
      return () => {
        stopQueries();
        stopMaps();
      };
    },
    [queryClient],
  );
  const done = useSyncExternalStore(subscribe, () => isFestivalOffline(queryClient, eventId));
  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    [],
  );

  if (done && tile) {
    return (
      <span
        className="event-tile__join event-tile__join--going"
        role="img"
        aria-label="Downloaded"
        title="Downloaded"
      >
        <Check size={16} strokeWidth={2.6} aria-hidden="true" />
      </span>
    );
  }
  if (done) {
    return (
      <span
        className="list-index__pill list-index__tag list-index__tag--on list-index__tag--icon"
        role="img"
        aria-label="Downloaded"
        title="Downloaded"
      >
        <ArrowDownToLine size={13} aria-hidden="true" />
      </span>
    );
  }

  const download = async () => {
    if (!hasService()) {
      setState('failed'); // fail fast: nothing to fetch from
      return;
    }
    setState('saving');
    let timer;
    const ok = await Promise.race([
      downloadFestival(queryClient, eventId, userId).catch(() => false),
      new Promise((resolve) => {
        timer = setTimeout(() => resolve(false), DOWNLOAD_GIVE_UP_MS);
      }),
    ]);
    clearTimeout(timer);
    if (mountedRef.current) setState(ok ? 'idle' : 'failed');
  };

  const failed = state === 'failed';
  const modifier =
    state === 'saving' ? ' list-index__pill--saving' : failed ? ' list-index__pill--failed' : '';

  const iconSize = tile ? 16 : 13;
  return (
    <button
      type="button"
      className={
        tile
          ? `event-tile__join download-tile${state === 'saving' ? ' list-index__pill--saving' : ''}`
          : `list-index__pill list-index__tag list-index__tag--icon list-index__pill--get${modifier}`
      }
      onClick={download}
      disabled={state === 'saving'}
      aria-label={
        failed ? `Download of ${eventName} failed - try again` : `Download ${eventName} for offline`
      }
      title={failed ? 'Download failed - tap to retry' : 'Download for offline'}
    >
      {failed ? (
        <RotateCw size={tile ? 15 : 12} strokeWidth={tile ? 2.6 : 2} aria-hidden="true" />
      ) : (
        <ArrowDownToLine size={iconSize} strokeWidth={tile ? 2.6 : 2} aria-hidden="true" />
      )}
    </button>
  );
}
